// Sound cues for finished background work: the tone library, the per-event
// preferences, the shared AudioContext, and the approval-nag repeat timers.
//
// Extracted from src/app.jsx (which stays the composition root and imports
// these back). Behaviour and markup are unchanged.

// ── Sound cues for finished background work ──────────────────────────────────
// Short alert tones synthesised with the Web Audio API — no audio asset is needed
// (keeps the no-build setup). Users choose a distinct sound per event (task
// finished / awaiting approval) in the Sounds settings, and toggle each one on or
// off individually; each choice is one entry from SOUND_LIBRARY. Any
// audio failure (autoplay policy, unsupported browser) is swallowed — the sound is
// a nicety, never a blocker.
const SOUND_LIBRARY={
  chime:  {label:"Chime",       type:"sine",     notes:[660,880,1175],         dur:0.14},
  bell:   {label:"Bell",        type:"triangle", notes:[988,1319],             dur:0.30},
  arp:    {label:"Arpeggio",    type:"triangle", notes:[523,659,784,1047],     dur:0.11},
  harp:   {label:"Harp",        type:"triangle", notes:[523,659,784,1047,1319],dur:0.08},
  ping:   {label:"Ping",        type:"sine",     notes:[1319],                 dur:0.18},
  sonar:  {label:"Sonar",       type:"sine",     notes:[1319],                 dur:0.42},
  twinkle:{label:"Twinkle",     type:"sine",     notes:[1047,1319,1568,2093],  dur:0.09},
  marimba:{label:"Marimba",     type:"triangle", notes:[784,1047],             dur:0.12},
  success:{label:"Success",     type:"sine",     notes:[784,988,1319],         dur:0.13},
  fanfare:{label:"Fanfare",     type:"triangle", notes:[523,659,784,1047,1047],dur:0.12},
  glass:  {label:"Glass",       type:"sine",     notes:[1568,2093],            dur:0.22},
  chirp:  {label:"Chirp",       type:"triangle", notes:[1568,2093],            dur:0.07},
  blip:   {label:"Blip",        type:"square",   notes:[520,780],              dur:0.08},
  pop:    {label:"Pop",         type:"sine",     notes:[392,587],              dur:0.10},
  boop:   {label:"Boop",        type:"sine",     notes:[523,392],              dur:0.12},
  coin:   {label:"Coin",        type:"square",   notes:[988,1319],             dur:0.09},
  retro:  {label:"Retro",       type:"square",   notes:[330,660,990],          dur:0.08},
  laser:  {label:"Laser",       type:"sawtooth", notes:[1200,600],             dur:0.12},
  drop:   {label:"Drop",        type:"sine",     notes:[660,440,330],          dur:0.10},
  knock:  {label:"Knock",       type:"sine",     notes:[196,147],              dur:0.09},
  alert:  {label:"Alert",       type:"square",   notes:[988,988],              dur:0.12},
  descend:{label:"Descend",     type:"triangle", notes:[1047,784,523],         dur:0.13},
  buzz:   {label:"Buzz",        type:"sawtooth", notes:[440,330],              dur:0.22},
  none:   {label:"Off (silent)",type:"sine",     notes:[],                     dur:0},
};
// The error cue is fixed (a low buzz) so a failed run always reads as "something
// went wrong" regardless of the per-event choice.
const ERROR_SOUND="buzz";
const SOUND_PREFS_KEY="hub.soundPrefs";
// Global mute — a single switch (toggled from the header bell) that silences ALL
// in-app notification cues regardless of the per-event flags. Persisted so the
// choice survives reloads; a `hub-notif-muted` window event fires on change so
// open UIs (the bell) can react live.
const NOTIF_MUTED_KEY="hub.notifMuted";
function loadNotifMuted(){try{return localStorage.getItem(NOTIF_MUTED_KEY)==="1";}catch(e){return false;}}
function saveNotifMuted(m){
  try{localStorage.setItem(NOTIF_MUTED_KEY,m?"1":"0");}catch(e){}
  try{window.dispatchEvent(new CustomEvent("hub-notif-muted",{detail:{muted:!!m}}));}catch(e){}
}
// Each notification event has a chosen sound (`<key>`) plus an individual on/off
// flag (`<key>On`). There is no global mute — muting is per-row. `needsApproval`
// additionally carries a repeat flag + interval (the nag). These are the
// in-app (foreground) side of the unified Notifications settings; the phone-push
// side lives server-side in settings.json (see NotificationsSettings).
const DEFAULT_SOUND_PREFS={
  runFinished:"chime",  runFinishedOn:true,
  needsApproval:"alert",needsApprovalOn:true,needsApprovalRepeat:true,needsApprovalRepeatSecs:60,
  taskDone:"success",   taskDoneOn:true,
  skillUsed:"voice",    skillUsedOn:true,
  volume:0.8,
};
// Migrate the pre-unification key names (task/approval/skill) to the canonical
// notification event keys, so an existing user keeps their sound choices.
function migrateSoundPrefs(v){
  if(!v||typeof v!=="object")return v;
  const map={task:"runFinished",taskOn:"runFinishedOn",approval:"needsApproval",approvalOn:"needsApprovalOn",approvalRepeat:"needsApprovalRepeat",skill:"skillUsed",skillOn:"skillUsedOn"};
  const out={...v};
  for(const oldK of Object.keys(map)){const newK=map[oldK];if(oldK in out){if(!(newK in out))out[newK]=out[oldK];delete out[oldK];}}
  return out;
}
function loadSoundPrefs(){
  try{const v=migrateSoundPrefs(JSON.parse(localStorage.getItem(SOUND_PREFS_KEY)));if(v&&typeof v==="object")return{...DEFAULT_SOUND_PREFS,...v};}catch(e){}
  return{...DEFAULT_SOUND_PREFS};
}
function saveSoundPrefs(p){try{localStorage.setItem(SOUND_PREFS_KEY,JSON.stringify(p));}catch(e){}}
// ONE shared AudioContext for every cue. Browsers start a context "suspended"
// until the page has seen a user gesture, and notes scheduled onto a suspended
// context are simply never heard. Building a fresh context per cue (as this once
// did) meant a hub reload followed by a task finishing while you were in another
// tab produced total silence — with the catch{} below hiding it. Reuse one
// context, unlock it on the first gesture, and resume before every cue.
let _audioCtx=null;
function audioCtx(){
  const AC=window.AudioContext||window.webkitAudioContext;if(!AC)return null;
  if(!_audioCtx){try{_audioCtx=new AC();}catch(e){return null;}}
  // A context can fall back to "suspended" (tab discarded, OS audio change), so
  // resume on every use, not just at creation.
  if(_audioCtx.state==="suspended"){try{_audioCtx.resume();}catch(e){}}
  return _audioCtx;
}
// Satisfy the autoplay policy: the first real user gesture unlocks the shared
// context so later cues (which fire with no gesture of their own) are audible.
function unlockAudio(){
  const kick=()=>{audioCtx();};
  try{
    window.addEventListener("pointerdown",kick,{once:false,passive:true});
    window.addEventListener("keydown",kick,{once:false,passive:true});
  }catch(e){}
}
// Play a named sound from the library at the given volume (0-1).
function playSound(name,volume){
  try{
    const s=SOUND_LIBRARY[name];if(!s||!s.notes.length)return;
    const ctx=audioCtx();if(!ctx)return;
    const peak=Math.max(0.0001,0.3*(volume==null?0.8:volume));
    s.notes.forEach((f,i)=>{
      const t=ctx.currentTime+i*s.dur;
      const osc=ctx.createOscillator(),g=ctx.createGain();
      osc.type=s.type||"sine";osc.frequency.value=f;
      g.gain.setValueAtTime(0.0001,t);
      g.gain.exponentialRampToValueAtTime(peak,t+0.02);
      g.gain.exponentialRampToValueAtTime(0.0001,t+s.dur);
      osc.connect(g);g.connect(ctx.destination);
      osc.start(t);osc.stop(t+s.dur);
    });
    // NB: the context is shared and deliberately left open — closing it here
    // would silence every cue that follows.
  }catch(e){}
}
// A special "sound" that speaks the event aloud (with the project name) via the
// Web Speech API instead of playing a synthesised tone. Selectable per event like
// any library tone; identified by this sentinel key.
const VOICE_SOUND="voice";
function soundLabel(k){return k===VOICE_SOUND?"🗣 Spoken (project name)":(SOUND_LIBRARY[k]&&SOUND_LIBRARY[k].label)||k;}
// Speak "Task completed, <project>" / "Task awaiting approval, <project>" aloud.
// Silently no-ops where speech synthesis is unavailable.
function speakAnnouncement(event,label,volume){
  try{
    const synth=window.speechSynthesis;
    if(!synth||typeof window.SpeechSynthesisUtterance!=="function")return;
    const base=event==="needsApproval"?"Task awaiting approval":event==="skillUsed"?"Using skill":event==="taskDone"?"Task done":"Task completed";
    const text=label?base+", "+label:base;
    const u=new SpeechSynthesisUtterance(text);
    u.volume=volume==null?0.8:Math.max(0,Math.min(1,volume));
    try{synth.cancel();}catch(e){}
    synth.speak(u);
  }catch(e){}
}
// Play the cue for an event ("task" | "approval"). Each event has its own on/off
// flag (`<event>On`); a disabled event stays silent. `ok===false` plays the fixed
// error buzz; otherwise the user's chosen sound for that event — a synthesised tone
// or, for the "voice" choice, a spoken announcement naming `label` (the project).
function playEventSound(event,ok,label){
  if(loadNotifMuted())return;
  const p=loadSoundPrefs();
  if(p[event+"On"]===false)return;
  if(ok===false)return playSound(ERROR_SOUND,p.volume);
  const choice=p[event]||DEFAULT_SOUND_PREFS[event]||"chime";
  if(choice===VOICE_SOUND)return speakAnnouncement(event,label,p.volume);
  playSound(choice,p.volume);
}
// One-shot cue dedup, shared by every caller (run stream + Web Push echo).
// The service worker re-posts a suppressed push to every focused window, so the
// same finish used to play twice (and once more per extra tab / push endpoint).
// `key` scopes the dedup (chat id, or the push tag); a repeat of the same
// key+event inside CUE_DEDUP_MS is swallowed. Returns true if it actually played.
const CUE_DEDUP_MS=20000;
const recentCues=new Map();
function fireCue(key,event,ok,label){
  const now=Date.now(),k=(key||"-")+"|"+event+"|"+(ok===false?0:1);
  for(const[kk,ts]of recentCues)if(now-ts>CUE_DEDUP_MS*3)recentCues.delete(kk);
  if(now-(recentCues.get(k)||0)<CUE_DEDUP_MS)return false;
  recentCues.set(k,now);
  playEventSound(event,ok,label);
  return true;
}
// Repeat for a repeat-capable event (today: needsApproval): after the initial
// cue, if the parked prompt still hasn't been answered, replay the same cue
// every `<event>RepeatSecs` seconds until it's resolved (ask_resolved / run end
// clears it). Keyed by session id so concurrent prompts nag independently;
// `label` is spoken when the chosen cue is the voice option.
const repeatTimers={};        // session id -> pending setTimeout
function repeatSecsFor(event){const p=loadSoundPrefs();const s=+p[event+"RepeatSecs"];return s>0?s:60;}
function startRepeat(event,id,label){
  stopRepeat(id);
  if(loadNotifMuted())return;
  const ms=repeatSecsFor(event)*1000;
  const tick=()=>{repeatTimers[id]=setTimeout(()=>{
    playEventSound(event,true,label);
    tick();
  },ms);};
  tick();
}
function stopRepeat(id){
  if(repeatTimers[id]!=null){clearTimeout(repeatTimers[id]);delete repeatTimers[id];}
}

export {SOUND_LIBRARY, ERROR_SOUND, SOUND_PREFS_KEY, DEFAULT_SOUND_PREFS,
  migrateSoundPrefs, loadSoundPrefs, saveSoundPrefs, audioCtx, unlockAudio, playSound,
  VOICE_SOUND, soundLabel, speakAnnouncement, playEventSound, fireCue, CUE_DEDUP_MS, repeatSecsFor,
  startRepeat, stopRepeat, NOTIF_MUTED_KEY, loadNotifMuted, saveNotifMuted};
